
<template>
    <v-container fluid>
        <template v-if="pageloading">
            <v-container fluid
                         fill-height
                         class="d-flex align-center justify-center" style="position:absolute;z-index:99999;background-color:rgba(0,0,0,.5)">
                <v-card tile>
                    <div class="text-center px-10 py-10">
                        <v-progress-circular :size="50"
                                             :width="5"
                                             :indeterminate="true"
                                             color="primary"></v-progress-circular>
                        <div class="mt-4 elevation-0">
                            <v-label>{{$t("loading")}}</v-label>
                        </div>
                    </div>
                </v-card>
            </v-container>
        </template>
        <v-card class="elevation-3" :loading="loading" v-if="!published">
            <div class="card-title">{{ cardTitle }}</div>
            <v-card-text v-if="!posted">
                <v-form ref="surveyForm" v-model="valid" lazy-validation>
                    <v-card tile class="my-4 elevation-0">
                        <v-card-text>
                            <!-- First Row: Always Visible -->
                            <v-row>
                                <v-col sm="6" md="6" cols="12">
                                    <v-text-field dense outlined v-model.lazy="survey.nameAr" :rules="nameRules" :label="$t('SurveyNameAr')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" @change="handleCopyValue('survey.nameAr', 'survey.nameEn')" />
                                </v-col>
                                <v-col sm="3" md="3" cols="12" style="display:none">
                                    <v-text-field dense outlined v-model.lazy="survey.nameEn" :rules="nameRules" :label="$t('SurveyNameEn')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                </v-col>
                                
                                <v-col sm="4" md="4" cols="12">
                                    <StructureSelect :outlined="outlined" dense v-model.lazy="survey.structureId" :rules="structureRules" :label="$t('Structure')" :initiation="true" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                </v-col>
                                <v-col sm="2" md="2" cols="12">
                                    <v-switch v-model="makeAllQuestionsRequired" class="ms-4 mt-0 text-start" :label="$t('MakeAllQuestionsRequired')" color="teal" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" @change="toggleAllQuestionsRequired" />
                                </v-col>
                            </v-row>
                            
                            <!-- Toggle Button -->
                            <v-row>
                                <v-col cols="12" class="text-center py-0">
                                    <v-btn text small color="primary" @click="showAdditionalFields = !showAdditionalFields" class="mb-2">
                                        <v-icon small>{{ showAdditionalFields ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                        <span class="mx-2">{{ showAdditionalFields ? $t('HideAdditionalFields') : $t('ShowAdditionalFields') }}</span>
                                        <v-icon small>{{ showAdditionalFields ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                    </v-btn>
                                </v-col>
                            </v-row>

                            <!-- Collapsible Section: Additional Fields -->
                            <v-expand-transition>
                                <div v-show="showAdditionalFields">
                                    <v-row>

                                        <v-col sm="3" md="3" cols="12">
                                            <DatePicker dense outlined v-model.lazy="survey.startDate" :label="$t('StartDate')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                        </v-col>
                                        <v-col sm="3" md="3" cols="12">
                                            <DatePicker dense outlined v-model.lazy="survey.expiryDate" :label="$t('ExpiryDate')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                        </v-col>
                                        <v-col sm="3" md="3" cols="12">
                                            <v-autocomplete searchable
                                                            multiple
                                                            clearable
                                                            outlined
                                                            dense
                                                            v-model.lazy="selectedMunicipalityItems"
                                                            :items="itemsMunicipality"
                                                            item-value="id"
                                                            item-text="municipalityName"
                                                            :menu-props="{ maxHeight: '150' }"
                                                            :label="$t('sharedWithMunicipality')">
                                                <template v-slot:prepend-item>
                                                    <v-list-item @click="selectAllItems">
                                                        <v-list-item-content>{{ $t('SelectAll') }}</v-list-item-content>
                                                    </v-list-item>
                                                </template>
                                                <template v-slot:item="{ item }">
                                                    <v-list-item-content>
                                                        <h4>{{ item.municipalityName }}</h4>
                                                    </v-list-item-content>
                                                </template>
                                                <template v-slot:selection="{ item }">
                                                    <v-chip small :key="item.id" close @click:close="removeItem(item.id)">
                                                        {{ item.municipalityName }}
                                                    </v-chip>
                                                </template>
                                            </v-autocomplete>
                                        </v-col>
                                        <v-col sm="4" md="4" cols="12">
                                            <v-autocomplete searchable dense outlined clearable v-model.lazy="survey.analyticsTypeId" :items="analyticsTypes" :menu-props="{ bottom: true, offsetY: true }" append-icon="mdi-chevron-down" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" item-value="id" :item-text="language == 'ar' ? 'nameAr' : 'nameEn'" :label="$t('SelectAnalyticsType')">
                                                <template v-slot:item="{ item }">
                                                    <v-icon class="me-2" color="primary">{{ item.iconMdi }}</v-icon>
                                                    <v-list-item-content>
                                                        <h4>{{ language == 'ar' ?  item.nameAr: item.nameEn }}</h4>
                                                    </v-list-item-content>
                                                </template>
                                            </v-autocomplete>
                                        </v-col>
                                        <v-col sm="6" md="6" cols="12">
                                            <v-textarea v-model.lazy="survey.description" outlined rows="3" :label="$t('Description')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                        </v-col>

                                        <v-col sm="2" md="2" cols="12">
                                            <v-switch v-model.lazy="survey.showOnHomePage" class="ms-4 mt-0 text-start" :label="$t('ShowOnHomePage')" color="teal" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                        </v-col>
                                        <v-col sm="2" md="2" cols="12">
                                            <v-switch v-model.lazy="survey.gisIntegration" class="ms-4 mt-0 text-start" :label="$t('GisIntegration')" color="teal" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                        </v-col>
                                        <v-col sm="4" md="4" cols="12" v-if="survey.gisIntegration">
                                            <v-text-field 
                                                v-model.lazy="gisIntegrationTitle" 
                                                outlined 
                                                dense 
                                                :label="$t('GisIntegrationTitle')" 
                                                :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)"
                                                @input="updateGisIntegrationTitles"
                                            />
                                        </v-col>
                                        <v-col sm="2" md="2" cols="12" v-if="survey.avgTime">
                                            <v-text-field dense outlined v-model="survey.avgTime" :rules="nameRules" :label="$t('OldSurveyAvgTime')" :disabled="true" />
                                        </v-col>
                                    </v-row>
                                </div>
                            </v-expand-transition>
                        </v-card-text>
                    </v-card>
                    <v-row>
                        <v-col cols="12">
                            <v-card v-for="(page, pageIndex) in survey.pages" :key="'p' + pageIndex" tile class="mt-4 elevation-5" color="grey lighten-3">
                                <v-toolbar dense color="primary" class="text-center white--text">
                                    <v-spacer></v-spacer>
                                    <h2> {{ $t('Page')}}  {{ pageIndex +1 }}</h2>
                                    <v-spacer></v-spacer>
                                    <v-btn icon class="text-center white--text" v-if="!isDisabled && pageIndex > 0" @click="removePage(pageIndex)">
                                        <v-icon>mdi-trash-can-outline</v-icon>
                                    </v-btn>
                                </v-toolbar>
                                <v-card-text>
                                    <v-row class="mt-3">
                                        <v-col cols="6">
                                            <v-text-field dense outlined v-model.lazy="page.nameAr" :label="$t('PageNameAr')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" @change="handleCopyValue(`survey.pages.${pageIndex}.nameAr`, `survey.pages.${pageIndex}.nameEn`)" />
                                        </v-col>
                                        <v-col cols="6"  style="display:none">
                                            <v-text-field dense outlined v-model.lazy="page.nameEn" :label="$t('PageNameEn')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                        </v-col>
                                    </v-row>

                                    <Draggable :list="page.questions" class="p-4" :options="draggableQuestionOptions" @end="updateAllPagesItemOrder">
                                        <transition-group dense>
                                            <v-expansion-panels v-for="(question, index) in page.questions" :key="getQuestionKey(question, pageIndex, index)" >
                                                <v-expansion-panel tile class="mt-4 elevation-0 drag" color="grey lighten-5">
                                                    <v-expansion-panel-header background-color="#fefefe" style="background: #01536314 !important">
                                                        <v-row class="mt-2">
                                                            <v-col cols="8" class="mt-1" v-html="'<b>' + question.questionAr+'</b>'"></v-col>
                                                            <v-col cols="2" md="2" class="text-center py-0">
                                                                <v-checkbox v-model.lazy="question.isRequired" @click.stop :label="$t('Required')" class="ma-2 mx-4" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)"></v-checkbox>
                                                            </v-col>
                                                            <v-col cols="2" md="2" class="text-center py-0">
                                                                <v-text-field v-model="question.analyticsOrderBy" outlined dense background-color="white" @click.stop :label="$t('AnalyticsOrder')" class="ma-2 mx-4" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)"></v-text-field>
                                                            </v-col>
                                                        </v-row>

                                                    </v-expansion-panel-header>
                                                    <v-expansion-panel-content>
                                                        <v-card class="elevation-0">
                                                            <div class="text-center">
                                                                <v-icon size="35" color="primary">mdi-drag-horizontal</v-icon>
                                                                <span style="float:left">
                                                                    <v-tooltip bottom v-if="question.questionTypeId">
                                                                        <template v-slot:activator="{ on, attrs }">
                                                                            <v-btn small fab class="ma-3" v-bind="attrs" v-on="on" @click="selectedQuestionTypeId = question.questionTypeId; previewDialog = true ">
                                                                                <v-icon color="primary">
                                                                                    mdi-eye
                                                                                </v-icon>
                                                                            </v-btn>
                                                                        </template>
                                                                        <span>{{ $t('Preview') }}</span>
                                                                    </v-tooltip>

                                                                    <v-btn small fab v-if="!survey.isPublished || hasUpdatePublishedPermission" @click="removeQuestion(page, index)" class="ma-5">
                                                                        <v-icon color="error">
                                                                            mdi-trash-can-outline
                                                                        </v-icon>
                                                                    </v-btn>
                                                                    <v-btn small fab v-if="!survey.isPublished || hasUpdatePublishedPermission" @click="duplicateQuestion(page, question)" class="ma-5">
                                                                        <v-icon color="secondary">
                                                                            mdi-content-duplicate
                                                                        </v-icon>
                                                                    </v-btn>
                                                                </span>
                                                            </div>

                                                            <v-card-text class="pt-0" style="clear:both">
                                                                <!-- First Row: Always Visible - Question Text -->
                                                                <v-row>
                                                                    <v-col cols="12" class="py-0">
                                                                        <div class="container">
                                                                            <TextEditor v-model="question.questionAr" :editable="!isDisabled && (!survey.isPublished || hasUpdatePublishedPermission)" :required="true" @input="handleCopyValue(`survey.pages.${pageIndex}.questions.${index}.questionAr`, `survey.pages.${pageIndex}.questions.${index}.questionEn`)" />
                                                                        </div>
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row>
                                                                    <v-col cols="12" md="6" class="py-0" style="display:none">
                                                                        <v-textarea dense outlined v-model.lazy="question.questionEn" append-icon="mdi-comment-question-outline" :rules="nameRules" :label="$t('QuestionEn')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                                                    </v-col>
                                                                    <v-col cols="6" md="6" class="py-0">
                                                                        <v-autocomplete searchable dense outlined v-model.lazy="question.questionTypeId" :items="questionTypes" :menu-props="{ bottom: true, offsetY: true }" :rules="nameRules" append-icon="mdi-chevron-down" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" item-value="id" :item-text="language == 'ar' ? 'questionTypeNameAr' : 'questionTypeNameEn'" :label="$t('Select')" @change="changeQuestionType(question, $event); changeRelatedQuestionType(page, question)">
                                                                            <template v-slot:item="{ item }">
                                                                                <v-icon class="me-2" color="primary">{{ item.iconMdi }}</v-icon>
                                                                                <v-list-item-content>
                                                                                    <h4>{{ language == 'ar' ?  item.questionTypeNameAr: item.questionTypeNameEn }}</h4>
                                                                                </v-list-item-content>
                                                                            </template>
                                                                        </v-autocomplete>
                                                                    </v-col>
                                                                    <v-col cols="6" md="6" class="py-0">
                                                                        <v-autocomplete searchable dense outlined clearable v-model.lazy="question.resultTypeId" :items="resultTypes" :menu-props="{ bottom: true, offsetY: true }" append-icon="mdi-chevron-down" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" item-value="id" :item-text="language == 'ar' ? 'nameAr' : 'nameEn'" :label="$t('SelectResultType')">
                                                                            <template v-slot:item="{ item }">
                                                                                <v-icon class="me-2" color="primary">{{ item.iconMdi }}</v-icon>
                                                                                <v-list-item-content>
                                                                                    <h4>{{ language == 'ar' ?  item.nameAr: item.nameEn }}</h4>
                                                                                </v-list-item-content>
                                                                            </template>
                                                                        </v-autocomplete>
                                                                    </v-col>
                                                                </v-row>

                                                                <!-- Toggle Button for Question Details -->
                                                                <v-row>
                                                                    <v-col cols="12" class="text-center py-0" style="margin-top:0px;margin-bottom:22px">
                                                                        <v-btn text x-small color="primary" @click="toggleQuestionDetails(pageIndex, index)" class="mb-2">
                                                                            <v-icon small>{{ question.showDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                                                            <span class="mx-2">{{ question.showDetails ? $t('HideQuestionDetails') : $t('ShowQuestionDetails') }}</span>
                                                                            <v-icon small>{{ question.showDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                                                        </v-btn>
                                                                    </v-col>
                                                                </v-row>

                                                                <!-- Collapsible Section: Additional Question Fields -->
                                                                <v-expand-transition>
                                                                    <div v-show="question.showDetails">
                                                                        <v-row>
                                                                            <v-col cols="12" md="6" class="py-0">
                                                                                <v-text-field dense outlined v-model.lazy="question.noteAr" append-icon="mdi-comment-outline" :label="$t('NoteAr')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" @change="handleCopyValue(`survey.pages.${pageIndex}.questions.${index}.noteAr`, `survey.pages.${pageIndex}.questions.${index}.noteEn`)" />
                                                                            </v-col>
                                                                            <v-col cols="12" md="6" class="py-0" style="display:none">
                                                                                <v-text-field dense outlined v-model.lazy="question.noteEn" append-icon="mdi-comment-outline" :label="$t('NoteEn')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                                                            </v-col>
                                                                            <v-col cols="12" md="6" class="py-0">
                                                                                <v-text-field dense outlined v-model.lazy="question.analyticsNameAr" append-icon="mdi-comment-question-outline" :label="$t('AnalyticsNameAr')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" @change="handleCopyValue(`survey.pages.${pageIndex}.questions.${index}.analyticsNameAr`, `survey.pages.${pageIndex}.questions.${index}.analyticsNameEn`)" />
                                                                            </v-col>
                                                                            <v-col cols="12" md="6" class="py-0" style="display:none">
                                                                                <v-text-field dense outlined v-model.lazy="question.analyticsNameEn" append-icon="mdi-comment-question-outline" :label="$t('AnalyticsNameEn')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                                                            </v-col>
                                                                            <v-col cols="6" md="6" class="py-0">
                                                                                <v-autocomplete searchable dense outlined clearable v-model.lazy="question.iconTypeId" :items="icons" :menu-props="{ bottom: true, offsetY: true }" append-icon="mdi-chevron-down" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" item-value="id" :item-text="language == 'ar' ? 'iconTypeNameAr' : 'iconTypeNameEn'" :label="$t('QuestionIconType')">
                                                                                    <template v-slot:item="{ item }">
                                                                                        <v-icon class="me-2" color="primary">{{ item.icon }}</v-icon>
                                                                                        <v-list-item-content>
                                                                                            <h4>{{ language == 'ar' ?  item.iconTypeNameAr: item.iconTypeNameEn }}</h4>
                                                                                        </v-list-item-content>
                                                                                    </template>
                                                                                </v-autocomplete>
                                                                            </v-col>
                                                                            <v-col cols="6" md="6" class="py-0">
                                                                                <v-file-input :rules="[fileRules]"
                                                                                              dense outlined
                                                                                              :label="$t('AnalyticsLogo')"
                                                                                              accept="image/*"
                                                                                              prepend-icon="mdi-image"
                                                                                              @change="handleAnalyticsImageUpload($event, question)" />
                                                                                <v-img v-if="question.analyticsImage"
                                                                                       :src="question.analyticsImage"
                                                                                       class="mt-3"
                                                                                       max-width="50px" />
                                                                            </v-col>
                                                                        </v-row>
                                                                    </div>
                                                                </v-expand-transition>
                                                            </v-card-text>
                                                            <v-card-actions>
                                                                <v-expansion-panels class="mb-4">
                                                                    <v-expansion-panel key="panel1">
                                                                        <v-expansion-panel-header background-color="#fefefe">
                                                                            <b>{{ $t('Settings') }}</b>

                                                                        </v-expansion-panel-header>
                                                                        <v-expansion-panel-content>
                                                                            <!--shortAnswer textArea-->
                                                                            <v-text-field class="mt-3" v-if="question.questionTypeId == shortAnswer || question.questionTypeId == textArea" outlined v-model.lazy="question.default" hide-detail dense :label="$t('DefaultText')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />

                                                                            <!--YesNo-->
                                                                            <LikeDislike :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" v-if="question.questionTypeId == yesNo"></LikeDislike>

                                                                            <!--BipolarMatrix-->
                                                                            <bipolar-matrix :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" v-if="question.questionTypeId == bipolarMatrix" :label="$t('BipolarMatrix')" v-model="question.questions"></bipolar-matrix>
                                                                            <!--TextEditor-->
                                                                            <div v-if="question.questionTypeId == textEditor" class="container">
                                                                                <TextEditor v-model="question.questionTypeParams" />
                                                                            </div>
                                                                            <!--Image-->
                                                                            <div class="text-center mt-3" v-if="question.questionTypeId == image" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)">
                                                                                <v-row>
                                                                                    <v-col cols="12" sm="12" md="6">
                                                                                        <!-- File input for image selection -->
                                                                                        <input 
                                                                                            type="file"
                                                                                            accept="image/*"
                                                                                            @change="onImageFileChange($event, question)"
                                                                                            :ref="'fileInput_' + question.id"
                                                                                            style="display: none"
                                                                                        />
                                                                                        
                                                                                        <v-btn rounded class="my-2" outlined @click="$refs['fileInput_' + question.id][0].click()">
                                                                                            <v-icon size="20" class="me-3">mdi-upload</v-icon>
                                                                                            {{$t('Upload')}}
                                                                                        </v-btn>

                                                                                        <!-- Image Cropper Dialog -->
                                                                                        <v-dialog v-model="question.showCropper" max-width="900px" persistent>
                                                                                            <v-card>
                                                                                                <v-card-title class="primary white--text">
                                                                                                    <v-icon color="white" class="me-2">mdi-crop</v-icon>
                                                                                                    {{$t('CropImage')}}
                                                                                                    <v-spacer></v-spacer>
                                                                                                    <v-btn icon dark @click="closeCropper(question)">
                                                                                                        <v-icon>mdi-close</v-icon>
                                                                                                    </v-btn>
                                                                                                </v-card-title>
                                                                                                <v-card-text class="pt-4">
                                                                                                    <cropper
                                                                                                        v-if="question.cropperImage"
                                                                                                        :src="question.cropperImage"
                                                                                                        :stencil-props="{
                                                                                                            aspectRatio: NaN,
                                                                                                            movable: true,
                                                                                                            resizable: true
                                                                                                        }"
                                                                                                        :canvas="{
                                                                                                            maxWidth: 1920,
                                                                                                            maxHeight: 1920
                                                                                                        }"
                                                                                                        :ref="'cropper_' + question.id"
                                                                                                        class="cropper-wrapper"
                                                                                                    />
                                                                                                    
                                                                                                    <!-- Zoom Control -->
                                                                                                    <v-row class="mt-4 align-center">
                                                                                                        <v-col cols="12">
                                                                                                            <v-subheader class="px-0">
                                                                                                                <v-icon class="me-2">mdi-magnify</v-icon>
                                                                                                                {{$t('Zoom')}}
                                                                                                            </v-subheader>
                                                                                                            <v-slider
                                                                                                                v-model="question.cropperZoom"
                                                                                                                @input="onZoomChange(question)"
                                                                                                                :min="0"
                                                                                                                :max="2"
                                                                                                                :step="0.01"
                                                                                                                prepend-icon="mdi-magnify-minus"
                                                                                                                append-icon="mdi-magnify-plus"
                                                                                                                hide-details
                                                                                                            ></v-slider>
                                                                                                        </v-col>
                                                                                                    </v-row>

                                                                                                    <!-- Rotation Controls -->
                                                                                                    <v-row class="mt-2 align-center">
                                                                                                        <v-col cols="12">
                                                                                                            <v-subheader class="px-0">
                                                                                                                <v-icon class="me-2">mdi-rotate-right</v-icon>
                                                                                                                {{$t('Rotate')}}
                                                                                                            </v-subheader>
                                                                                                            <v-btn-toggle v-model="question.rotationAngle" mandatory class="d-flex flex-wrap">
                                                                                                                <v-btn small @click="rotateImage(question, -90)">
                                                                                                                    <v-icon>mdi-rotate-left</v-icon>
                                                                                                                    <span class="ms-1">-90°</span>
                                                                                                                </v-btn>
                                                                                                                <v-btn small @click="rotateImage(question, 90)">
                                                                                                                    <v-icon>mdi-rotate-right</v-icon>
                                                                                                                    <span class="ms-1">+90°</span>
                                                                                                                </v-btn>
                                                                                                                <v-btn small @click="flipImageHorizontal(question)">
                                                                                                                    <v-icon>mdi-flip-horizontal</v-icon>
                                                                                                                    <span class="ms-1">{{$t('FlipH')}}</span>
                                                                                                                </v-btn>
                                                                                                                <v-btn small @click="flipImageVertical(question)">
                                                                                                                    <v-icon>mdi-flip-vertical</v-icon>
                                                                                                                    <span class="ms-1">{{$t('FlipV')}}</span>
                                                                                                                </v-btn>
                                                                                                                <v-btn small @click="resetCropper(question)" color="warning">
                                                                                                                    <v-icon>mdi-restore</v-icon>
                                                                                                                    <span class="ms-1">{{$t('Reset')}}</span>
                                                                                                                </v-btn>
                                                                                                            </v-btn-toggle>
                                                                                                        </v-col>
                                                                                                    </v-row>

                                                                                                    <!-- Size Info -->
                                                                                                    <v-row class="mt-2">
                                                                                                        <v-col cols="12">
                                                                                                            <v-chip small outlined class="me-2">
                                                                                                                <v-icon small class="me-1">mdi-information</v-icon>
                                                                                                                {{$t('DragToResize')}}
                                                                                                            </v-chip>
                                                                                                        </v-col>
                                                                                                    </v-row>
                                                                                                </v-card-text>
                                                                                                <v-divider></v-divider>
                                                                                                <v-card-actions class="pa-4">
                                                                                                    <v-spacer></v-spacer>
                                                                                                    <v-btn text large @click="closeCropper(question)">
                                                                                                        <v-icon class="me-1">mdi-close</v-icon>
                                                                                                        {{$t('Cancel')}}
                                                                                                    </v-btn>
                                                                                                    <v-btn color="primary" large @click="saveCroppedImage(question)">
                                                                                                        <v-icon class="me-1">mdi-check</v-icon>
                                                                                                        {{$t('Save')}}
                                                                                                    </v-btn>
                                                                                                </v-card-actions>
                                                                                            </v-card>
                                                                                        </v-dialog>

                                                                                        <v-row v-if="question.default">
                                                                                            <v-slider v-model="zoom" @change="createImage(zoom, question)"
                                                                                                      append-icon="mdi-magnify-plus-outline"
                                                                                                      prepend-icon="mdi-magnify-minus-outline"></v-slider>
                                                                                        </v-row>
                                                                                    </v-col>
                                                                                    <v-col cols="6">
                                                                                        <v-select v-model="question.imagePosition" class="mt-1"
                                                                                                  item-text="name"
                                                                                                  item-value="value"
                                                                                                  :items="positions"
                                                                                                  :label="$t('SelectPosition')">
                                                                                        </v-select>
                                                                                    </v-col>
                                                                                </v-row>
                                                                                <v-row>
                                                                                    <v-col cols="12" sm="6" md="6">
                                                                                        <!-- Display the preview image next to the input file element -->
                                                                                        <v-img v-if="question.default" :ref="'img_'+question.id" :src="question.default" height="100%" :width="questionImgWidth(question.imageWidth)" :alt="previewMedia" class="preview-image"></v-img>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </div>

                                                                            <!--vedioLink-->
                                                                            <v-text-field class="mt-3" v-if="question.questionTypeId == vedioLink" outlined v-model="question.default" hide-detail dense :label="$t('VedioLink')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />

                                                                            <!--NPS-->
                                                                            <div class="text-center" v-if="question.questionTypeId == nps">
                                                                                <NPS :increaseDecrease="true" v-model="question.questionTypeParams"></NPS>
                                                                            </div>

                                                                            <!--Consent-->
                                                                            <div class="text-center mt-3" v-if="question.questionTypeId == Consent">
                                                                                <v-text-field outlined v-model="question.questionTypeParams" hide-detail dense />
                                                                            </div>

                                                                            <!--Ranking-->
                                                                            <div class="text-center mt-3" v-if="question.questionTypeId == Ranking">
                                                                                <Ranking outlined v-model="question.questionTypeParams" hide-detail dense></Ranking>
                                                                            </div>

                                                                            <!--PhoneNumber-->
                                                                            <phoneNumber :isAdmin="true" class="mt-3" v-if="question.questionTypeId == PhoneNumber" outlined v-model="question.questionTypeParams" />

                                                                            <!--ContactForm-->
                                                                            <ContactForm :validate="false" :isAdmin="false" class="mt-3" v-if="question.questionTypeId == ContactForm" outlined />

                                                                            <!--PictureChoice-->
                                                                            <PictureChoice @input="handleImageChange" @imageRemoved="handleImageRemoved" class="mt-3" v-if="question.questionTypeId == PictureChoice" outlined v-model="question.questionPictures" />

                                                                            <!--EmailInput-->
                                                                            <emailInput class="mt-3" :required="false" v-if="question.questionTypeId == EmailInput" outlined v-model="question.selectedValue" />

                                                                            <!--Date-->
                                                                            <div class="text-center" v-if="question.questionTypeId == Date">
                                                                                <DatePicker v-model="question.questionTypeParams"></DatePicker>
                                                                            </div>

                                                                            <!--Url-->
                                                                            <div class="text-center" v-if="question.questionTypeId == Url">
                                                                                <Url v-model="question.questionTypeParams"></Url>
                                                                            </div>

                                                                            <!--UploadFiles-->
                                                                            <div class="text-center" v-if="question.questionTypeId == UploadFiles">
                                                                                <UploadArrayCheckboxList v-model="question.questionTypeParams" :divStyle="'display:inline-flex'" :checkboxStyle="'margin-left:25px;'" :options="fileUploadOptions" dense />
                                                                            </div>

                                                                            <!--Hijri Date-->
                                                                            <div class="text-center" v-if="question.questionTypeId == HijriDate">
                                                                                <!--<hijriDatePicker></hijriDatePicker>-->
                                                                                <hijriDatePicker v-model="question.questionTypeParams"></hijriDatePicker>
                                                                            </div>


                                                                            <!--Rating-->
                                                                            <template v-if="question.questionTypeId == rating">
                                                                                <v-row class="mt-10">
                                                                                    <v-col md="3"><v-select dense outlined v-model="question.questionTypeParams" :items="['Star','Emoji']"></v-select></v-col>
                                                                                    <v-col md="9">
                                                                                        <v-rating v-if="question.questionTypeParams === 'Star'" v-model.lazy="rated" :length="rateLength" large></v-rating>
                                                                                        <SmileyRating v-if="question.questionTypeParams ==='Emoji'"></SmileyRating>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </template>

                                                                            <!--Label-->
                                                                            <v-text-field class="mt-3" v-if="question.questionTypeId == label" outlined v-model.lazy="question.default" hide-detail dense :label="$t('Text')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                                                            <!--PredefineDropdown-->
                                                                            <template v-if="question.questionTypeId == PredefineDropdown">
                                                                                <v-row>
                                                                                    <v-col md="3">
                                                                                        <v-select class="mt-4" :rules="nameRules" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" v-model="question.lookupId" dense :items="itemsForDropdown(question)" :label="$t('StartWith')" outlined item-text="name" item-value="id" @change="handleSelectionChange(question, $event)"></v-select>
                                                                                    </v-col>
                                                                                    <v-col v-if="dependentItems.length > 0" md="3">
                                                                                        <v-select class="mt-4" :rules="nameRules" v-model="question.questionTypeParams" dense :items="itemsForDropdownRelated(question)" :label="$t('StopWith')" outlined item-text="name" item-value="id"></v-select>
                                                                                    </v-col>
                                                                                </v-row>
                                                                                <v-row>

                                                                                </v-row>
                                                                            </template>

                                                                            <!--multipleChoice checkbox dropdown-->
                                                                            <template v-if="question.questionTypeId == multipleChoice || question.questionTypeId == checkbox || question.questionTypeId == dropdown|| question.questionTypeId == trickyQuestion">
                                                                                <v-row>
                                                                                    <v-col cols="5">
                                                                                        <v-row v-for="(item, optIndex) in question.options" :key="optIndex" class="mt-2">
                                                                                            <v-col md="8">
                                                                                                <v-text-field outlined v-model.lazy="item.optionAr" hide-details dense :label="$t('TextAr')" :rules="nameRules" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" @change="handleCopyValue(`survey.pages.${pageIndex}.questions.${index}.options.${optIndex}.optionAr`, `survey.pages.${pageIndex}.questions.${index}.options.${optIndex}.optionEn`)" />
                                                                                            </v-col>
                                                                                            <v-col md="3" style="display:none">
                                                                                                <v-text-field outlined v-model.lazy="item.optionEn" hide-details dense :label="$t('TextEn')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                                                                            </v-col>

                                                                                            <v-col md="1" v-if="question.questionTypeId == trickyQuestion">
                                                                                                <v-checkbox outlined v-model="item.isCorrect" dense :label="$t('IsCorrectAnswer')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                                                                            </v-col>
                                                                                            <v-col md="3">
                                                                                                <v-text-field outlined v-model.lazy="item.order" hide-details dense :label="$t('Order')" :rules="nameRules" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                                                                            </v-col>
                                                                                            <v-col md="1">
                                                                                                <v-icon class="mt-2" @click="removeOption(optIndex, question.options)" v-if="!survey.isPublished  || hasUpdatePublishedPermission">
                                                                                                    mdi-close
                                                                                                </v-icon>
                                                                                            </v-col>
                                                                                        </v-row>
                                                                                    </v-col>
                                                                                    <v-col cols="7" style="align-content:flex-end">
                                                                                        <v-row>
                                                                                            <v-col cols="12" md="12" class="text-start">
                                                                                                <v-btn rounded v-if="!survey.isPublished  || hasUpdatePublishedPermission" class="mt-2" outlined @click="addOption(question)">
                                                                                                    <v-icon size="20" class="me-3">mdi-plus-circle-outline</v-icon>   {{ $t('AddOptions') }}
                                                                                                </v-btn>
                                                                                            </v-col>
                                                                                        </v-row>
                                                                                    </v-col>
                                                                                </v-row>
                                                                                
                                                                                
                                                                            </template>
                                                                            <template v-if="question.questionTypeId == CSAT5 || question.questionTypeId == CSAT3">

                                                                                <v-row v-for="(item, optIndex) in question.options" :key="optIndex" class="mt-2">
                                                                                    <v-col md="1">
                                                                                        <v-icon size="50" :color="item.color" class="me-3">{{item.icon}}</v-icon>
                                                                                    </v-col>
                                                                                    <v-col md="2">
                                                                                        <v-text-field outlined v-model.lazy="item.optionAr" hide-details dense :label="$t('TextAr')" :rules="nameRules" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" @change="handleCopyValue(`survey.pages.${pageIndex}.questions.${index}.options.${optIndex}.optionAr`, `survey.pages.${pageIndex}.questions.${index}.options.${optIndex}.optionEn`)" />
                                                                                    </v-col>
                                                                                    <v-col md="2" style="display:none">
                                                                                        <v-text-field outlined v-model.lazy="item.optionEn" hide-details dense :label="$t('TextEn')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                                                                    </v-col>

                                                                                    
                                                                                    <v-col md="2">
                                                                                        <v-text-field outlined v-model.lazy="item.order" hide-details dense :label="$t('Order')" :rules="nameRules" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                                                                    </v-col>
                                                                                    <v-col md="1">
                                                                                        
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </template>
                                                                        </v-expansion-panel-content>
                                                                    </v-expansion-panel>
                                                                    <v-expansion-panel key="panel2" style="margin-top:10px">
                                                                        <v-expansion-panel-header background-color="#fefefe">
                                                                            <b>{{ $t('Logic') }}</b>

                                                                        </v-expansion-panel-header>
                                                                        <v-expansion-panel-content>
                                                                            <v-row style="margin-top:10px">
                                                                                <v-col md="12">
                                                                                    <h3>{{ $t('DisplayLogic') }}</h3>
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row v-for="(l, lindex) in question.logic" :key="'l' + lindex">
                                                                                <v-col md="12">

                                                                                    <v-btn fab v-if="!survey.isPublished || hasUpdatePublishedPermission" small color="error" @click="removeLogic(question, lindex)" class="mb-4">
                                                                                        <v-icon size="20">
                                                                                            mdi-minus
                                                                                        </v-icon>
                                                                                    </v-btn>
                                                                                    <div style="clear:both"></div>
                                                                                    <table style="width:100%">
                                                                                        <tr>
                                                                                            <td v-if="lindex>0"><v-select class="mt-4" v-if="lindex>0" v-model="l.operand" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" dense :items="[{ code: 'and', text: $t('And') }, { code: 'or', text: $t('Or') }]" :label="$t('Select')" outlined item-text="text" item-value="code"></v-select></td>
                                                                                            <td style="width:10px" v-if="lindex>0"></td>
                                                                                            <td><v-select class="mt-4" :value="l.relatedQuestionId === null || l.relatedQuestionId === undefined ? undefined : l.relatedQuestionId"  @change="handleRelatedQuestionChange(page, l, $event)" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" clearable dense :items="filteredQuestionsForLogic(page, question)" :label="$t('DisplayTheQuestionOnlyIf')" outlined item-text="questionAr" item-value="reference"></v-select></td>
                                                                                        </tr>
                                                                                    </table>


                                                                                </v-col>
                                                                                <v-col md="6" v-if="l.relatedQuestionId!=null">
                                                                                    <v-select class="mt-4" v-model="l.operator" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" dense :items="filteredOperandForLogic(page, l)" :label="$t('Select')" outlined item-text="text" item-value="code"></v-select>
                                                                                </v-col>
                                                                                <v-col md="6" v-if="l.relatedQuestionId!=null">
                                                                                    <v-text-field class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == shortAnswer || l.relatedQuestionTypeId  == textArea" outlined hide-detail dense :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                                                                    <v-select class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == yesNo" dense outlined :label="$t('Select')" item-text="text" item-value="code" :items="[{code:'yes',text:$t('Yes')},{code:'no',text:$t('No')}]"></v-select>
                                                                                    <v-select class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == checkbox || l.relatedQuestionTypeId == multipleChoice || l.relatedQuestionTypeId == dropdown || l.relatedQuestionTypeId == bipolarMatrix|| l.relatedQuestionTypeId == trickyQuestion" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" dense :items="filteredRelatedQuestionOptions(page, l)" :label="$t('Select')" outlined item-text="optionAr" item-value="optionAr"></v-select>
                                                                                    <v-select class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == PredefineDropdown" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" dense :items="getpredefinedItems(page, l)" :label="$t('Select')" outlined item-text="name" item-value="id"></v-select>
                                                                                    <v-select class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == nps" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" dense :items="filteredRelatedQuestionTypeParams(page, l)" :label="$t('Select')" outlined item-text="optionAr" item-value="optionAr"></v-select>
                                                                                    <v-select class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == rating" dense outlined :label="$t('Select')" item-text="text" item-value="code" :items="['1','2','3','4','5']"></v-select>
                                                                                    <v-text-field class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == number" outlined hide-detail dense :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" />
                                                                                    <div v-if="l.relatedQuestionTypeId == date" class="mt-4"><DatePicker dense outlined v-model="l.value" :rules="question.required ? nameRules : []" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)" /></div>
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col md="12">
                                                                                    <v-btn fab v-if="!survey.isPublished || hasUpdatePublishedPermission" small color="primary" @click="addLogic(question)" class="mb-4">
                                                                                        <v-icon size="20">
                                                                                            mdi-plus
                                                                                        </v-icon>
                                                                                    </v-btn>
                                                                                </v-col>

                                                                            </v-row>
                                                                        </v-expansion-panel-content>
                                                                    </v-expansion-panel>
                                                                </v-expansion-panels>
                                                            </v-card-actions>
                                                        </v-card>
                                                    </v-expansion-panel-content>
                                                </v-expansion-panel>
                                            </v-expansion-panels>
                                        </transition-group>
                                    </Draggable>
                                    <v-row>
                                        <v-col cols="12" class="text-center">
                                            <v-btn tile small color="primarylight" style="position:relative;z-index:999999" class="mt-3 black--text" dark @click="addQuestion(page)" v-if="!survey.isPublished || hasUpdatePublishedPermission">
                                                {{$t('AddQuestion')}}
                                                <v-icon>mdi-head-question</v-icon>
                                            </v-btn>
                                            <v-btn tile small color="primary" style="position:relative;z-index:999999" class="mt-3 mx-4" dark @click="openQuestionsBankDialog(page)" v-if="!survey.isPublished || hasUpdatePublishedPermission">
                                                {{$t('AddFromQuestionsBank')}}
                                                <v-icon>mdi-library-outline</v-icon>
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row class="mt-3">
                        <v-col cols="12" class="text-center">
                            <h3></h3>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn fab v-if="!survey.isPublished || hasUpdatePublishedPermission" small color="primary" @click="addSection" v-bind="attrs" v-on="on" class="mb-4">
                                        <v-icon size="20">
                                            mdi-file-plus-outline
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ $t('AddPage') }}</span>
                            </v-tooltip>
                        </v-col>
                    </v-row>
                </v-form>

                <v-bottom-navigation background-color="secondary" dark shift v-if="!survey.isPublished || hasUpdatePublishedPermission">
                    <v-btn v-if="!isEdit" style="height:100%" tile @click="save" :loading="btnLoading" color="secondary">
                        {{ $t('Save') }}
                        <v-icon>
                            mdi-floppy
                        </v-icon>
                    </v-btn>

                    <v-btn v-if="isEdit" style="height:100%" tile @click="save" :loading="btnLoading" color="secondary">
                        {{ $t('Update') }}
                        <v-icon>
                            mdi-pencil
                        </v-icon>
                    </v-btn>
                    <v-btn tile @click="publish" style="height:100%" :loading="btnLoading" v-if="!survey.isPublished" color="secondary">
                        {{ $t('Start') }}
                        <v-icon>
                            mdi-power
                        </v-icon>
                    </v-btn>
                    <v-btn tile @click="preview" style="height:100%" :loading="btnLoading" color="secondary">
                        {{ $t('Preview') }}
                        <v-icon>
                            mdi-account-eye
                        </v-icon>
                    </v-btn>
                </v-bottom-navigation>
            </v-card-text>

            <v-card-text v-else>
                <v-alert dense text type="success">
                    {{ $t('SaveSuccess') }}
                </v-alert>
                <v-row v-if="formattedTime">
                    <v-col cols="12" v-if="oldAvgTime">
                        <v-text-field dense outlined v-model="oldAvgTime" :rules="nameRules" :label="$t('OldSurveyAvgTime')" :disabled="true" />
                    </v-col>
                    <v-col cols="12">
                        <v-text-field dense outlined v-model="formattedTime" :rules="nameRules" :label="$t('SurveyAvgTime')" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)"
                                      readonly
                                      @focus="if(!isDisabled || (survey.isPublished && hasUpdatePublishedPermission)){openTimePicker = true}" />
                    </v-col>
                </v-row>
                <v-card-actions class="">
                    <!--<router-link v-slot="{ navigate }" to="/survey-list" custom>-->
                    <v-btn role="link" tile color="primary" class="white--text" @click="updateTimeAndNavigate">
                        {{ $t('SurveyListBack') }}
                    </v-btn>
                    <!--</router-link>-->
                </v-card-actions>
            </v-card-text>
            <ControlPreview v-model="previewDialog" v-if="previewDialog" :questionTypeId="selectedQuestionTypeId"></ControlPreview>
        </v-card>

        <v-card tile v-if="published">
            <v-card-text class="pb-0">
                <h2 class="text--primary mb-3">{{ $t('SuccessStartingProcess') }}</h2>

                <p class="text--primary">
                    <b>{{ $t('ProcessNumber') }}</b>
                    : {{ response != null ? response.processInstanceId : '' }}
                </p>
                <p class="text--primary">
                    <b>{{ $t('ReferenceNumber') }}</b>
                    : {{ response != null ? response.referenceNumber : '' }}
                </p>
            </v-card-text>
            <v-card-actions class="pt-0">
                <v-btn text color="teal accent-4" to="/">{{ $t('Home') }}</v-btn>
            </v-card-actions>
        </v-card>
        <CommonDialog v-model="openQuestionsbank" :fullscreen="false" width="600" :title="$t('AddFromQuestionsBank')" show-action-toolbar>
            <v-card-text>
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-select v-model="selectedQuestionBank" :items="questionsbanks" item-text="questionTitle" item-value="id" :label="$t('Question')" />
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
            <template v-slot:actions>
                <v-btn color="primary darken-1"
                       @click="mapQuestionsBank()">
                    {{$t('Save')}}
                </v-btn>
                <v-btn color="error"
                       @click="openQuestionsbank = false">
                    {{$t('Cancel')}}
                </v-btn>
            </template>
        </CommonDialog>
        <v-dialog v-model="openTimePicker" v-if="openTimePicker" width="400">
            <v-card>
                <v-card-title>
                    <span class="headline">Select Time</span>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col>
                            <v-text-field v-model.number="hours"
                                          label="Hours"
                                          type="number"
                                          min="0"
                                          outlined></v-text-field>
                        </v-col>
                        <v-col>
                            <v-text-field v-model.number="minutes"
                                          label="Minutes"
                                          type="number"
                                          min="0"
                                          max="59"
                                          :rules="[value => (value >= 0 && value <= 59) || 'Must be between 0 and 59']"
                                          outlined></v-text-field>
                        </v-col>
                        <v-col>
                            <v-text-field v-model.number="seconds"
                                          label="Seconds"
                                          type="number"
                                          min="0"
                                          max="59"
                                          :rules="[value => (value >= 0 && value <= 59) || 'Must be between 0 and 59']"
                                          outlined></v-text-field>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeTimePicker">{{$t('OK')}}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    import { mapGetters } from 'vuex'
    import CommonDialog from '@/components/controls/CommonDialog'
    import SurveyService from '@/services/SurveyService.js'
    import StructuresService from '@/services/StructuresService.js'
    import UsersService from '@/services/UsersService.js'
    import ProcessesService from '@/services/ProcessesService.js'
    import LookupAdminService from '@/services/LookupAdminServices.js'
    import QuestionsBankService from '@/services/QuestionsBankService.js'
    import DatePicker from '@/components/controls/DatePicker.vue'
    import StructureSelect from '@/components/controls/StructureSelect.vue'
    import VTextField from '@/components/controls/VTextField.vue'
    import ControlPreview from '@/components/controls/ControlPreview.vue'
    import BipolarMatrix from '@/components/controls/BipolarMatrix.vue'
    import SmileyRating from '@/components/controls/SmileyRating.vue'
    import LikeDislike from '@/components/controls/LikeDislike.vue'
    import NPS from '@/components/controls/NPS.vue'
    import hijriDatePicker from '@/components/controls/HijriParent.vue'
    import phoneNumber from '@/components/controls/phoneNumber.vue'
    import emailInput from '@/components/controls/EmailInput.vue'
    import PictureChoice from '@/components/controls/PictureChoice.vue'
    import ContactForm from '@/pages/Admin/ContactForm.vue'
    import Url from '@/components/controls/url.vue'
    import { Cropper } from 'vue-advanced-cropper'
    import 'vue-advanced-cropper/dist/style.css'
    import UploadArrayCheckboxList from '@/components/controls/UploadArrayCheckboxList.vue'
    import TextEditor from '@/components/controls/TextEditor.vue'
    import Ranking from '@/components/controls/RankingItems.vue'


    import Draggable from 'vuedraggable'
    import { FormMethodEnum, ProcessTypesEnum } from '@/helpers/constants/enumerations.js'
    import { createGuid, useStripHtml, copyValue } from '@/helpers/common.js'

    export default {
        components: {
            DatePicker, TextEditor, CommonDialog, hijriDatePicker, phoneNumber, emailInput, PictureChoice, Url, UploadArrayCheckboxList, ContactForm, StructureSelect, Draggable, ControlPreview, VTextField, BipolarMatrix, SmileyRating, LikeDislike, NPS, Ranking, Cropper
        },
        props: {
            isDisabled: {
                type: Boolean,
                default: false
            },
            surveyIdComp: {
                type: Number
            }
        },

        watch: {
            'survey.pages': {
                handler: function (val) {
                    if (!this.isFirstLoad) {
                        this.majorChange = true
                    }
                    else {
                        this.isFirstLoad = false
                    }
                },
                deep: true
            }
        },

        data() {
            return {
                showAdditionalFields: false, // Controls collapsible section visibility
                makeAllQuestionsRequired: false, // Controls whether all questions should be required
                gisIntegrationTitle: '', // Holds the GIS Integration Title
                draggableQuestionOptions: { group: 'survey-questions' },
                avgTime: null,
                hours: 0,
                minutes: 0,
                seconds: 0,
                openTimePicker:false,
                surveyId:null,
                oldAvgTime:null,
                pageloading:false,
                openQuestionsbank: false,
                selectedQuestionBank: null,
                questionsbanks: [],
                selectedPage: null,
                isFirstLoad: false,
                majorChange: false,
                response: null,
                nameRules: [(v) => !!v || this.$t('RequiredField')],

                structureRules: [(v) => !!v || this.$t('RequiredField')],
                fileRules: [
                    v => !v || ['image/jpeg', 'image/png', 'image/gif'].includes(v.type) || this.$t('InvalidFileType')
                ],
                questionTypes: [],
                resultTypes: [],
                analyticsTypes:[],
                stringFilters: [
                    'Contains',
                    'Does Not Contain',
                    'Starts With',
                    'Ends With',
                    'Is Answered',
                    'Is Not Answered',
                    'Equals',
                    'Not eEqual To',
                ],
                defaultValue: 100 + '%',
                imageWidth: 100,
                imagePosition: 'start',
                zoom: 100,
                slideValue: null,
                rateLength: 5,
                rated: 0,
                selectedQuestionTypeId: null,
                previewDialog: false,
                valid: true,
                published: false,
                outlined: true,
                loading: false,
                btnLoading: false,
                isEdit: false,
                posted: false,
                survey:{
                    id: null,
                    nameAr: '',
                    nameEn: '',
                    description: '',
                    isRequired: false,
                    isPublished: false,
                    showOnHomePage: false,
                    gisIntegration: false,
                    forSpecificParticipants: false,
                    structureId: null,
                    expiryDate: null,
                    startDate: null,
                    avgTime:null,
                    pages: [
                        {
                            number: 1,
                            nameAr: '',
                            nameEn: '',
                            questions: [],
                        }
                    ],
                },
                shortAnswer: FormMethodEnum.ShortAnswer,
                checkbox: FormMethodEnum.Checkboxes,
                multipleChoice: FormMethodEnum.MultipleChoice,
                dropdown: FormMethodEnum.Dropdown,
                trickyQuestion: FormMethodEnum.TrickyQuestion,
                rating: FormMethodEnum.Rating,
                number: FormMethodEnum.number,
                yesNo: FormMethodEnum.YesNo,
                date: FormMethodEnum.Date,
                textArea: FormMethodEnum.TextArea,
                bipolarMatrix: FormMethodEnum.BipolarMatrix,
                PredefineDropdown: FormMethodEnum.PredefineDropdown,
                textEditor: FormMethodEnum.TextEditor,
                image: FormMethodEnum.Image,
                vedioLink: FormMethodEnum.VedioLink,
                label: FormMethodEnum.Label,
                nps: FormMethodEnum.NPS,
                HijriDate: FormMethodEnum.HijriDate,
                PhoneNumber: FormMethodEnum.PhoneNumber,
                EmailInput: FormMethodEnum.EmailInput,
                PictureChoice: FormMethodEnum.PictureChoice,
                ContactForm: FormMethodEnum.ContactForm,
                Url: FormMethodEnum.Url,
                UploadFiles: FormMethodEnum.UploadFiles,
                Consent: FormMethodEnum.Consent,
                Ranking: FormMethodEnum.Ranking,
                CSAT5: FormMethodEnum.CSAT5,
                CSAT3: FormMethodEnum.CSAT3,
                lookups: [],
                selectedItem: [],
                uploadMessagePlaceHolder: this.$t('Upload'),
                selectedFile: null,
                previewMedia: this.$t('Preview'),
                hasUpdatePublishedPermission: false,
                dependentItems: [],
                cardTitle: this.$t('AddSurvey'),
                show: true,
                params: {
                    token: '123456798',
                    name: 'avatar'
                },
                headers: {
                    smail: '*_~'
                },
                icons:[],
                imgDataUrl: '',
                //fileSizeLimit: 2,
                fileUploadOptions: [{ 'id': 1, 'name': 'PDF' }, { 'id': 2, 'name': 'DOC' }, { 'id': 3, 'name': 'DOCX' }, { 'id': 4, 'name': 'PNG' }, { 'id': 5, 'name': 'JPG' }, { 'id': 6, 'name': 'JPEG' }, { 'id': 7, 'name': 'GIF' }],
                selectedMunicipalityItems: [],
                itemsMunicipality: [],
                positions: [{ name: this.$t('StartPos'), value: 'start' }, { name: this.$t('End'), value: 'end' }, { name: this.$t('Middle'), value: 'middle' }],
            }
        },

        created() {
            this.isFirstLoad = true
            this.listQuestionIcons()
            this.getquestionTypes()
            this.getresultTypes()
            this.getanalyticsTypes()
            
            QuestionsBankService.listQuestions().then((res) => {
                this.questionsbanks = res.data
                this.questionsbanks.forEach(e => {
                    var questionText = (this.language == 'ar' ? e.questionAr : e.questionEn)
                    questionText = questionText ? useStripHtml(questionText) : questionText
                    switch (e.questionTypeId) {
                        case FormMethodEnum.ShortAnswer:
                            e.questionTitle = questionText + ' (' + this.$t('ShortAnswer') + ')'
                            break
                        case FormMethodEnum.MultipleChoice:
                            e.questionTitle = questionText + ' (' + this.$t('MultipleChoice') + ')'
                            break
                        case FormMethodEnum.Checkboxes:
                            e.questionTitle = questionText + ' (' + this.$t('Checkboxes') + ')'
                            break
                        case FormMethodEnum.Dropdown:
                            e.questionTitle = questionText + ' (' + this.$t('Dropdown') + ')'
                            break
                        case FormMethodEnum.TrickyQuestion:
                            e.questionTitle = questionText + ' (' + this.$t('TrickyQuestion') + ')'
                            break
                        case FormMethodEnum.Rating:
                            e.questionTitle = questionText + ' (' + this.$t('Rating') + ')'
                            break
                        case FormMethodEnum.number:
                            e.questionTitle = questionText + ' (' + this.$t('number') + ')'
                            break
                        case FormMethodEnum.YesNo:
                            e.questionTitle = questionText + ' (' + this.$t('YesNo') + ')'
                            break
                        case FormMethodEnum.Date:
                            e.questionTitle = questionText + ' (' + this.$t('Date') + ')'
                            break
                        case FormMethodEnum.TextArea:
                            e.questionTitle = questionText + ' (' + this.$t('TextArea') + ')'
                            break
                        case FormMethodEnum.TextEditor:
                            e.questionTitle = questionText + ' (' + this.$t('TextEditor') + ')'
                            break
                        case FormMethodEnum.BipolarMatrix:
                            e.questionTitle = questionText + ' (' + this.$t('BipolarMatrix') + ')'
                            break
                        case FormMethodEnum.PredefineDropdown:
                            e.questionTitle = questionText + ' (' + this.$t('PredefineDropdown') + ')'
                            break
                        case FormMethodEnum.Image:
                            e.questionTitle = questionText + ' (' + this.$t('Image') + ')'
                            break
                        case FormMethodEnum.VedioLink:
                            e.questionTitle = questionText + ' (' + this.$t('VedioLink') + ')'
                            break
                        case FormMethodEnum.NPS:
                            e.questionTitle = questionText + ' (' + this.$t('NPS') + ')'
                            break
                        case FormMethodEnum.Maps:
                            e.questionTitle = questionText + ' (' + this.$t('Maps') + ')'
                            break
                        case FormMethodEnum.Label:
                            e.questionTitle = questionText + ' (' + this.$t('Label') + ')'
                            break
                        case FormMethodEnum.HijriDate:
                            e.questionTitle = questionText + ' (' + this.$t('HijriDate') + ')'
                            break
                        case FormMethodEnum.PhoneNumber:
                            e.questionTitle = questionText + ' (' + this.$t('PhoneNumber') + ')'
                            break
                        case FormMethodEnum.EmailInput:
                            e.questionTitle = questionText + ' (' + this.$t('EmailInput') + ')'
                            break
                        case FormMethodEnum.PictureChoice:
                            e.questionTitle = questionText + ' (' + this.$t('PictureChoice') + ')'
                            break
                        case FormMethodEnum.ContactForm:
                            e.questionTitle = questionText + ' (' + this.$t('ContactForm') + ')'
                            break
                        case FormMethodEnum.Url:
                            e.questionTitle = questionText + ' (' + this.$t('Url') + ')'
                            break
                        case FormMethodEnum.UploadFiles:
                            e.questionTitle = questionText + ' (' + this.$t('UploadFiles') + ')'
                            break
                        case FormMethodEnum.Consent:
                            e.questionTitle = questionText + ' (' + this.$t('Consent') + ')'
                            break
                        default:
                            e.questionTitle = questionText
                            break
                    }
                })
                
            })
            if (this.$route.query.id) {
                this.isEdit = true
                this.loadSurvey(this.$route.query.id)
                this.hasUpdatePermission()

            }
            else if (this.surveyIdComp != undefined && this.surveyIdComp != null) {
                this.loadSurvey(this.surveyIdComp)
                this.cardTitle = this.$t('Survey')
            }

            this.loadLookups()
            this.getMunicipality()
        },

        computed: {
            ...mapGetters('user', ['language']),
            getLanguage() {
                return this.language == null ? 'ar' : this.language

            },
            formattedTime() {
                // Format the selected time as hh:mm:ss for display
                return `${(this.hours >= 0 ? this.hours.toString().padStart(2, '0') : '00')}:${(this.minutes >= 0 && this.minutes <= 59 ? this.minutes
                    .toString()
                    .padStart(2, '0') : '00')}:${(this.seconds >= 0 && this.seconds <= 59 ? this.seconds.toString().padStart(2, '0') : '00')}`
            },
            itemsForDropdown() {
                return (currentQuestion) => {
                    LookupAdminService.listSurveyLookupItems(currentQuestion.lookupId)
                        .then((res) => {
                            currentQuestion.lookupoptions = []
                            res.data.forEach((el) => {
                                currentQuestion.lookupoptions.push({ id: el.id.toString(), name: el.name })
                            })
                        })
                        .catch((error) => {
                            //this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => {
                            //this.loading = false
                        })
                    var language = this.getLanguage
                    return this.lookups.map(item => ({ id: item.id, name: language == 'ar' ? item.nameAr : item.nameEn }))
                }
            },
            itemsForDropdownRelated() {
                return (currentQuestion) => {
                    LookupAdminService.listSurveyLookupItems(currentQuestion.lookupId)
                        .then((res) => {
                            currentQuestion.lookupoptions = []
                            res.data.forEach((el) => {
                                currentQuestion.lookupoptions.push({ id: el.id.toString(), name: el.name })
                            })
                        })
                        .catch((error) => {
                            //this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => {
                            //this.loading = false
                        })
                    var language = this.getLanguage
                    return this.dependentItems.map(item => ({ id: item.id.toString(), name: language == 'ar' ? item.nameAr : item.nameEn }))
                }
            },
            itemsForAllDropDown() {
                var language = this.getLanguage
                var all = []
                this.lookups.forEach(item => {
                    all.push({ id: item.id.toString(), name: language == 'ar' ? item.nameAr : item.nameEn })
                })
                this.dependentItems.forEach(item => {
                    all.push({ id: item.id.toString(), name: language == 'ar' ? item.nameAr : item.nameEn })
                })
                return all
            },
            filteredQuestionsForLogic() {
                return (page, question) => {
                    var allqs = []
                    for (var i = 0; i < this.survey.pages.length; i++) {
                        var p = this.survey.pages[i]
                        for (var j = 0; j < p.questions.length; j++) {
                            var q = p.questions[j]
                            allqs.push({
                                ...q,
                                questionAr: q.questionAr ? useStripHtml(q.questionAr) : q.questionAr,
                                questionEn: q.questionEn ? useStripHtml(q.questionEn) : q.questionEn,
                                pageId: p.number
                            })
                        }
                    }

                    return allqs.filter(x => (x.pageId == page.number && x.orderBy < question.orderBy) || x.pageId < page.number)

                }
            },
            filteredRelatedQuestionTypeParams() {
                return (page, logic) => {
                    var allqs = []
                    for (var i = 0; i < this.survey.pages.length; i++) {
                        var p = this.survey.pages[i]
                        for (var j = 0; j < p.questions.length; j++) {
                            var q = p.questions[j]
                            allqs.push({ ...q, pageId: p.number })
                        }
                    }
                    if (page && logic) {
                        var relatedquestion = allqs.filter(x => x.reference == logic.relatedQuestionId)[0]
                        var bpoptions = []
                        var number = relatedquestion.questionTypeParams.split('#')[2]
                        if (number) {
                            for (let i = 0; i <= number; i++) {
                                bpoptions.push({ optionAr: i.toString(), optionEn: i.toString() })
                            }
                        }
                        return bpoptions
                    }
                    else {
                        return []
                    }
                }
            },
            filteredRelatedQuestionOptions() {
                return (page, logic) => {
                    var allqs = []
                    for (var i = 0; i < this.survey.pages.length; i++) {
                        var p = this.survey.pages[i]
                        for (var j = 0; j < p.questions.length; j++) {
                            var q = p.questions[j]
                            allqs.push({ ...q, pageId: p.number })
                        }
                    }
                    if (page && logic) {
                        var relatedquestion = allqs.filter(x => x.reference == logic.relatedQuestionId)[0]
                        if (logic.relatedQuestionTypeId == this.bipolarMatrix) {
                            var bpoptions = []
                            for (let q of relatedquestion.questions) {
                                for (let op of q.options) {
                                    const questionAr = q.questionAr ? useStripHtml(q.questionAr) : q.questionAr
                                    const questionEn = q.questionEn ? useStripHtml(q.questionEn) : q.questionEn
                                    const optionAr = op.optionAr ? useStripHtml(op.optionAr) : op.optionAr
                                    const optionEn = op.optionEn ? useStripHtml(op.optionEn) : op.optionEn
                                    bpoptions.push({ optionAr: questionAr + ':' + optionAr, optionEn: questionEn + ':' + optionEn })
                                }

                            }
                            return bpoptions
                        }
                        else {
                            return (relatedquestion.options || []).map(op => ({
                                ...op,
                                optionAr: op.optionAr ? useStripHtml(op.optionAr) : op.optionAr,
                                optionEn: op.optionEn ? useStripHtml(op.optionEn) : op.optionEn
                            }))
                        }
                    }
                    else {
                        return []
                    }
                }
            },
            getpredefinedItems() {
                return (page, logic) => {
                    var allqs = []
                    for (var i = 0; i < this.survey.pages.length; i++) {
                        var p = this.survey.pages[i]
                        for (var j = 0; j < p.questions.length; j++) {
                            var q = p.questions[j]
                            allqs.push({ ...q, pageId: p.number })
                        }
                    }
                    var relatedquestion = allqs.filter(x => x.reference == logic.relatedQuestionId)[0]
                    return relatedquestion.lookupoptions
                }
            },
            filteredOperandForLogic() {

                return (page, logic) => {

                    var allqs = []
                    for (var i = 0; i < this.survey.pages.length; i++) {
                        var p = this.survey.pages[i]
                        for (var j = 0; j < p.questions.length; j++) {
                            var q = p.questions[j]
                            allqs.push({ ...q, pageId: p.number })
                        }
                    }
                    const contains = this.$t('Contains')
                    const notcontains = this.$t('DoesNotContain')
                    const startswith = this.$t('StartsWith')
                    const endswith = this.$t('EndsWith')
                    const isanswered = this.$t('IsAnswered')
                    const isnotanswered = this.$t('IsNotAnswered')
                    const equal = this.$t('EqualTo')
                    const notequal = this.$t('NotEqualTo')
                    const respondentselected = this.$t('RespondentSelected')
                    const respondentnotselected = this.$t('RespondentNotSelected')
                    const lessthan = this.$t('LessThan')
                    const greaterthen = this.$t('GreaterThan')
                    const not = this.$t('Not')
                    const $is = this.$t('Is')
                    var relatedquestion = allqs.filter(x => x.reference == logic.relatedQuestionId)[0]
                    if (relatedquestion) {

                        switch (relatedquestion.questionTypeId) {
                            case FormMethodEnum.ShortAnswer:
                            case FormMethodEnum.TextArea:
                                return [{ code: 'contains', text: contains }, { code: 'notcontains', text: notcontains }, { code: 'starts', text: startswith }, { code: 'ends', text: endswith }, { code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }, { code: 'equal', text: equal }, { code: 'notequal', text: notequal }]
                                break
                            case FormMethodEnum.Checkboxes:
                            case FormMethodEnum.MultipleChoice:
                            case FormMethodEnum.Dropdown:
                            case FormMethodEnum.TrickyQuestion:
                            case FormMethodEnum.PredefineDropdown:
                                return [{ code: 'equal', text: respondentselected }, { code: 'notequal', text: respondentnotselected }, { code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }]
                                break
                            case FormMethodEnum.Rating:
                            case FormMethodEnum.number:
                            case FormMethodEnum.NPS:
                                return [{ code: 'lessthan', text: lessthan }, { code: 'greaterthan', text: greaterthen }, { code: 'equal', text: equal }, { code: 'notequal', text: notequal }, { code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }]
                                break
                            case FormMethodEnum.YesNo:
                                return [{ code: 'equal', text: $is }, { code: 'notequal', text: not }, { code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }]
                                break
                            case FormMethodEnum.Date:
                            case FormMethodEnum.BipolarMatrix:
                                return [{ code: 'equal', text: equal }, { code: 'notequal', text: notequal }, { code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }]
                                break
                            case FormMethodEnum.Image:
                            case FormMethodEnum.VedioLink:
                            case FormMethodEnum.Label:
                            case FormMethodEnum.Maps:
                                return [{ code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }]
                                break



                        }
                    }
                    else
                        return []
                }
            },

        },

        methods: {
            encodeText(value) {
                if (typeof value !== 'string' || !value) {
                    return value
                }
                try {
                    const encoded = btoa(unescape(encodeURIComponent(value)))
                    return '__ENC__' + encoded
                } catch (e) {
                    return value
                }
            },
            encodeQuestionForApi(question) {
                if (!question) return
                question.questionAr = this.encodeText(question.questionAr)
                question.questionEn = this.encodeText(question.questionEn)
                question.noteAr = this.encodeText(question.noteAr)
                question.noteEn = this.encodeText(question.noteEn)
                question.default = this.encodeText(question.default)
                question.questionTypeParams = this.encodeText(question.questionTypeParams)
                question.analyticsNameAr = this.encodeText(question.analyticsNameAr)
                question.analyticsNameEn = this.encodeText(question.analyticsNameEn)
                question.analyticsImage = this.encodeText(question.analyticsImage)
                question.reference = this.encodeText(question.reference)

                if (Array.isArray(question.options)) {
                    question.options.forEach((opt) => {
                        if (!opt) return
                        opt.optionAr = this.encodeText(opt.optionAr)
                        opt.optionEn = this.encodeText(opt.optionEn)
                        opt.icon = this.encodeText(opt.icon)
                    })
                }

                if (Array.isArray(question.questions)) {
                    question.questions.forEach((child) => this.encodeQuestionForApi(child))
                }
            },
            encodeSurveyForApi(survey) {
                if (!survey) return survey
                const payload = JSON.parse(JSON.stringify(survey))

                payload.nameAr = this.encodeText(payload.nameAr)
                payload.nameEn = this.encodeText(payload.nameEn)
                payload.description = this.encodeText(payload.description)
                payload.gisIntegrationTitleAr = this.encodeText(payload.gisIntegrationTitleAr)
                payload.gisIntegrationTitleEn = this.encodeText(payload.gisIntegrationTitleEn)

                if (Array.isArray(payload.pages)) {
                    payload.pages.forEach((page) => {
                        if (!page) return
                        page.nameAr = this.encodeText(page.nameAr)
                        page.nameEn = this.encodeText(page.nameEn)
                        if (Array.isArray(page.questions)) {
                            page.questions.forEach((q) => this.encodeQuestionForApi(q))
                        }
                    })
                }

                return payload
            },
            updateGisIntegrationTitles() {
                // Update both Arabic and English titles with the same value
                this.survey.gisIntegrationTitleAr = this.gisIntegrationTitle
                this.survey.gisIntegrationTitleEn = this.gisIntegrationTitle
            },

            async listQuestionIcons() {
                var result = await SurveyService.listQuestionIcons()
                this.icons = result.data
            },
            toggleAllQuestionsRequired() {
                // Loop through all pages and all questions to set isRequired
                this.survey.pages.forEach(page => {
                    page.questions.forEach(question => {
                        question.isRequired = this.makeAllQuestionsRequired
                    })
                })
            },
            parseTimeSpan(timeSpan) {
                // Assuming timeSpan is in "hh:mm:ss" format
                const parts = timeSpan.split(':')
                this.hours = parseInt(parts[0], 10)
                this.minutes = parseInt(parts[1], 10)
                this.seconds = parseInt(parts[2], 10)
                this.avgTime = timeSpan
                this.formattedTime = timeSpan // Update the formatted time
            },

            closeTimePicker() {
                if (this.hours >= 0 && this.minutes >= 0 && this.minutes <= 59 && this.seconds >= 0 && this.seconds <= 59) {
                    this.avgTime = this.formattedTime
                    this.openTimePicker = false
                }
            },
            async updateTimeAndNavigate() {
                await SurveyService.updateSurveyTime(this.surveyId, this.avgTime)
                this.$router.push('/survey-list')
            },
            setSelectedQuestionType(question) {
                this.selectedQuestionTypeId = question.questionTypeId
                this.previewDialog = true
            },
            questionImgWidth(width) {
                return width + '%'
            },
            handleImageChange(images) {
                if (images) {
                    this.question.questionPictures = images
                }
            },
            handleImageRemoved(removedImage) {
            },
            mapQuestionsBank() {
                var bank = this.questionsbanks.filter(x => x.id == this.selectedQuestionBank)[0]
                this.selectedPage.questions.push({
                    reference: createGuid(),
                    questionAr: bank.questionAr,
                    questionEn: bank.questionEn,
                    noteAr: bank.noteAr,
                    noteEn: bank.noteEn,
                    questionTypeId: bank.questionTypeId,
                    orderBy: this.selectedPage.questions.length + 1,
                    options: bank.options,
                    questions: bank.questions,
                    default: bank.default,
                    customConfiguration: null,
                    isRequired: bank.isRequired,
                    logic: [{
                        reference: createGuid(),
                        operand: null,
                        relatedQuestionTypeId: null,
                        relatedQuestionId: null,
                        operator: '',
                        value: ''
                    }],
                    //questionTypeParams: 'Star',
                    optionsRating: ['Star', 'Emoji'],
                    selectedRateOption: bank.questionTypeParams,
                    questionTypeParams: bank.questionTypeParams
                })
                this.openQuestionsbank = false
            },
            openQuestionsBankDialog(page) {
                this.selectedPage = page
                this.openQuestionsbank = true
            },
            loadDependentItemsForEdit() {
                this.survey.pages.forEach((page) => {
                    page.questions.forEach((question) => {
                        if (question.questionTypeId === this.PredefineDropdown) {
                            this.loadDependentItems(question.lookupId)
                        }
                    })
                })
            },
            changeRelatedQuestionType(page, question) {
                page.questions.forEach(e => {
                    e.logic.forEach(x => {
                        if (x.relatedQuestionId == question.reference) {
                            x.relatedQuestionTypeId = question.questionTypeId
                        }
                    })
                })
            },
            handleAnalyticsImageUpload(file, question) {
                if (file) {
                    const reader = new FileReader()
                    reader.onload = (e) => {
                        console.log('data', e.target.result)
                        question.analyticsImage = e.target.result
                    }
                    reader.readAsDataURL(file)
                } else {
                    question.analyticsImage = ''
                }
            },
            getquestionTypes() {
                LookupAdminService.listQuestionTypeItems().then((res) => {
                    this.questionTypes = res.data

                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            getresultTypes() {
                LookupAdminService.listResultTypeItems().then((res) => {
                    this.resultTypes = res.data

                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            getanalyticsTypes() {
                LookupAdminService.listAnalyticsTypeItems().then((res) => {
                    this.analyticsTypes = res.data

                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            CheckRatingOption(question) {
                question.questionTypeParams = question.selectedRateOption
            },
            addSection() {
                this.survey.pages.push({ number: this.survey.pages.length + 1, nameAr: '', nameEn: '', questions: [] })
            },
            addLogic(question) {
                question.logic.push({
                    reference: createGuid(),
                    operand: null,
                    relatedQuestionTypeId: null,
                    relatedQuestionId: null,
                    operator: '',
                    value: ''
                })
            },
            duplicateQuestion(page, question) {
                page.questions.push(
                    {
                        reference: createGuid(),
                        questionAr: question.questionAr,
                        questionEn: question.questionEn,
                        noteAr: question.noteAr,
                        NoteEn: question.NoteEn,
                        questionTypeId: question.questionTypeId,
                        orderBy: page.questions.length + 1,
                        analyticsOrderBy: null,
                        options: question.options,
                        questions: question.questions,
                        default: question.default,
                        customConfiguration: question.customConfiguration,
                        isRequired: question.isRequired,
                        logic: question.logic,
                        imageWidth: question.imageWidth,
                        imagePosition: question.imagePosition,
                        questionTypeParams: question.questionTypeParams,
                        optionsRating: question.optionsRating,
                        selectedRateOption: question.selectedRateOption,
                        resultTypeId: question.resultTypeId
                    })
            },
            addQuestion(page) {
                page.questions.push(
                    {
                        reference: createGuid(),
                        questionAr: '',
                        questionEn: '',
                        noteAr: '',
                        NoteEn: '',
                        questionTypeId: null,
                        orderBy: page.questions.length + 1,
                        analyticsOrderBy: null,
                        options: [],
                        questions: [],
                        default: null,
                        customConfiguration: null,
                        isRequired: false,
                        showDetails: false, // Controls collapsible question details
                        logic: [{
                            reference: createGuid(),
                            operand: null,
                            relatedQuestionTypeId: null,
                            relatedQuestionId: null,
                            operator: '',
                            value: ''
                        }],
                        imageWidth: this.imageWidth,
                        imagePosition: this.imagePosition,
                        questionTypeParams: '',
                        optionsRating: ['Star', 'Emoji'],
                        selectedRateOption: 'Star'
                    })
            },
            toggleQuestionDetails(pageIndex, questionIndex) {
                // Toggle the showDetails property for the specific question
                const question = this.survey.pages[pageIndex].questions[questionIndex]
                this.$set(question, 'showDetails', !question.showDetails)
            },
            getQuestionKey(question, pageIndex, index) {
                return question.reference || question.recordPK || 'q-' + pageIndex + '-' + index
            },
            updateAllPagesItemOrder() {
                if (!this.survey || !this.survey.pages) return
                this.survey.pages.forEach(page => this.updateItemOrder(page))
            },
            updateItemOrder: function (page) {
                if (!page || !page.questions) return
                page.questions.forEach((item, index) => {
                    item.orderBy = index + 1
                })
            },
            removeQuestion(page, index) {
                page.questions.splice(index, 1)
            },
            removePage(index) {
                this.survey.pages.splice(index, 1)
            },
            removeLogic(question, index) {
                question.logic.splice(index, 1)
            },
            addCSAT5Options(question) {
                question.options = []
                let option1 = { optionAr: 'راضٍ جداً', optionEn: 'Very Satisfied', order: 1, icon: 'mdi-emoticon-outline', color:'#14573A' }
                question.options.push(option1)
                let option2 = { optionAr: 'راضٍ', optionEn: 'Satisfied', order: 2, icon: 'mdi-emoticon-happy-outline', color: '#1B8354' }
                question.options.push(option2)
                let option3 = { optionAr: 'محايد', optionEn: 'Neutral', order: 3, icon: 'mdi-emoticon-neutral-outline', color: '#FEC84B' }
                question.options.push(option3)
                let option4 = { optionAr: 'غير راضٍ', optionEn: 'Not Satisfied', order: 4, icon: 'mdi-emoticon-sad-outline', color: '#F04438' }
                question.options.push(option4)
                let option5 = { optionAr: 'غير راضٍ مطلقًا', optionEn: 'No Satisfied at all', order: 5, icon: 'mdi-emoticon-dead-outline', color: '#B42318' }
                question.options.push(option5)
            },
            addCSAT3Options(question) {
                question.options = []
                let option1 = { optionAr: 'راضٍ', optionEn: 'Satisfied', order: 1, icon: 'mdi-emoticon-happy-outline', color: '#1B8354' }
                question.options.push(option1)
                let option2 = { optionAr: 'محايد', optionEn: 'Neutral', order: 2, icon: 'mdi-emoticon-neutral-outline', color: '#FEC84B' }
                question.options.push(option2)
                let option3 = { optionAr: 'غير راضٍ', optionEn: 'Not Satisfied', order: 3, icon: 'mdi-emoticon-sad-outline', color: '#F04438' }
                question.options.push(option3)
            },
            addOption(question) {
                let option = { optionAr: this.inputTextAr, optionEn: this.inputTextEn, order: question.options.length + 1 }
                question.options.push(option)
                this.inputTextAr = ''
                this.inputTextEn = ''
            },
            removeOption(index, options) {
                options.splice(index, 1)
            },
            loadLookups() {
                LookupAdminService.listSurveyLookups().then((res) => {
                    this.lookups = res.data

                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            handleSelectionChange(question, selected) {
                question.lookupId = selected
                this.predefineDropdownItemsPage(question)
                this.loadDependentItems(selected)
                LookupAdminService.listSurveyLookupItems(question.lookupId)
                    .then((res) => {
                        question.lookupoptions = []
                        res.data.forEach((el) => {
                            question.lookupoptions.push({ id: el.id.toString(), name: el.name })
                        })
                    })
                    .catch((error) => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
            },
            loadDependentItems(id) {
                LookupAdminService.listSurveyLookupsByParentId(id).then((res) => {
                    this.dependentItems = res.data

                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            save() {
                // Validate the form
                if (!this.$refs.surveyForm.validate()) {
                    //this.$root.$notification(this.$t('AddNameError'), 'error')
                    this.scrollToFirstInvalidField()
                    return
                }

                if (!this.survey.nameAr || !this.survey.nameEn) {
                    this.$root.$notification(this.$t('AddNameError'), 'error')
                    this.scrollToFirstInvalidField()
                    return
                }

                this.loading = true
                this.btnLoading = true
                this.updateGisIntegrationTitles()
                this.survey.nameAr = useStripHtml(this.survey.nameAr)
                this.survey.nameEn = useStripHtml(this.survey.nameEn)
                this.survey.shareWithMunicipalities = this.selectedMunicipalityItems.join(',')
                const payload = this.encodeSurveyForApi(this.survey)
                SurveyService.addSurvey(payload, this.majorChange).then((res) => {
                    this.$refs.surveyForm.resetValidation()
                    this.$refs.surveyForm.reset()
                    this.posted = true
                    this.surveyId = res.data.id
                    this.parseTimeSpan(res.data.avgTime)
                    this.oldAvgTime = res.data.oldAvgTime
                    this.performScrollToTop()
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                    this.btnLoading = false
                })
            },
            preview() {
                this.loading = true
                this.btnLoading = true
                // Ensure GIS Integration Title fields are updated before saving
                this.updateGisIntegrationTitles()
                const payload = this.encodeSurveyForApi(this.survey)
                SurveyService.addSurvey(payload, this.majorChange).then((res) => {
                    this.posted = true
                    this.surveyId = res.data.id
                    this.parseTimeSpan(res.data.avgTime)
                    this.oldAvgTime = res.data.oldAvgTime
                    this.$router.push('/form/' + res.data.id + '?viewMode=' + true)
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                    this.btnLoading = false
                })
            },
            publish() {
                
                if (this.validate()) {
                    this.loading = true
                    this.btnLoading = true
                    this.survey.shareWithMunicipalities = this.selectedMunicipalityItems.join(',')
                    // Ensure GIS Integration Title fields are updated before saving
                    this.updateGisIntegrationTitles()
                    const payload = this.encodeSurveyForApi(this.survey)
                    SurveyService.addSurvey(payload, this.majorChange).then((res) => {
                        if (res.data) {
                            this.surveyId = res.data.id
                            this.parseTimeSpan(res.data.avgTime)
                            this.oldAvgTime = res.data.oldAvgTime
                            this.startProcess(res.data.id)
                        }
                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                        this.btnLoading = false
                    })
                }
            },
            startProcess(surveyId) {
                if (surveyId) {
                    this.btnLoading = true
                    this.updateGisIntegrationTitles()
                    ProcessesService.createProcess(this.wrapForm(surveyId)).then((res) => {
                        if (res.data.success == false) {
                            this.$root.$notification(this.$t(res.data.message), 'error')
                            this.btnLoading = false
                        } else {
                            this.published = true
                            this.response = res.data
                            this.$root.$notification(this.$t('WorkflowStarted'), 'success')
                        }

                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
                }
            },
            wrapForm(surveyId) {
                let formdata = new FormData()
                formdata.append('objectData', JSON.stringify({
                    surveyId: surveyId,
                    structureId: this.survey.structureId
                }))
                formdata.append('processTypeId', ProcessTypesEnum.SurveyApproval.toString())
                formdata.append('structureId', this.survey.structureId)
                return formdata
            },
            validate() {
                let valid = true
                if (this.$refs.surveyForm.validate()) {

                    for (var i = 0; i < this.survey.pages.length; i++) {
                        let page = this.survey.pages[i]
                        if (page) {
                            if (page.questions.length == 0) {
                                this.$root.$notification(this.$t('AddQuestionRequired') + ` (${page.nameAr}) `, 'error')
                                valid = false
                            }

                            else {
                                for (var j = 0; j < page.questions.length; j++) {
                                    let question = page.questions[j]
                                    if (question) {

                                    }
                                }
                            }
                        }
                    }
                }
                else { 
                    valid = false
                    this.scrollToFirstInvalidField()
                }
                return valid
            },
            handleRelatedQuestionChange(page, logic, selectedValue) {
                // Only call setLogic if a value is actually selected (not null/undefined)
                
                if (selectedValue !== null && selectedValue !== undefined) {
                    this.setLogic(page, logic, selectedValue)
                } else {
                    // When cleared, ensure relatedQuestionTypeId is also null
                    logic.relatedQuestionId = null
                    logic.relatedQuestionTypeId = null
                }
            },
            setLogic(page, logic, selectedValue) {
                // Use the selected value from the change event
                var questionId = selectedValue
                
                // If relatedQuestionId is null, ensure it stays null and doesn't match any item
                if (questionId === null || questionId === undefined) {
                    logic.relatedQuestionId = null
                    logic.relatedQuestionTypeId = null
                    return
                }
                
                var allqs = []
                for (var i = 0; i < this.survey.pages.length; i++) {
                    var p = this.survey.pages[i]
                    for (var j = 0; j < p.questions.length; j++) {
                        var q = p.questions[j]
                        allqs.push({ ...q, pageId: p.number })
                    }
                }
                var relatedquestion = allqs.filter(x => x.reference == questionId)[0]
                if (relatedquestion) {
                    logic.relatedQuestionId = relatedquestion.reference
                    logic.relatedQuestionTypeId = relatedquestion.questionTypeId
                }
                else {
                    logic.relatedQuestionId = null
                    logic.relatedQuestionTypeId = null
                }
            },
            loadSurvey(surveyId) {
                this.loading = true
                this.pageloading = true
                SurveyService.loadSurvey(surveyId).then((res) => {
                    
                    this.survey = res.data
                    this.survey.nameAr = useStripHtml(res.data.nameAr)
                    this.survey.nameEn = useStripHtml(res.data.nameEn)
                    
                    // Initialize GIS Integration Title from loaded data
                    if (res.data.gisIntegrationTitleAr || res.data.gisIntegrationTitleEn) {
                        // Use the current language version or fallback to the other
                        this.gisIntegrationTitle = this.language === 'ar' 
                            ? (res.data.gisIntegrationTitleAr || res.data.gisIntegrationTitleEn)
                            : (res.data.gisIntegrationTitleEn || res.data.gisIntegrationTitleAr)
                    }
                    
                    // Initialize showDetails property for all existing questions
                    if (this.survey.pages) {
                        this.survey.pages.forEach(page => {
                            if (page.questions) {
                                page.questions.forEach(question => {
                                    this.$set(question, 'showDetails', false)
                                })
                            }
                        })
                    }
                    
                    if (res.data.shareWithMunicipalities) {
                        const municipalityItems = res.data.shareWithMunicipalities.split(',')
                        const miArrary = []
                        for (var i = 0; i < municipalityItems.length; i++) {
                            miArrary.push(Number(municipalityItems[i]))
                        }
                        this.selectedMunicipalityItems = miArrary
                    }
                    this.survey.id = surveyId
                    this.loadDependentItemsForEdit()
                    for (var i = 0; i < this.survey.pages.length; i++) {
                        let currentPage = this.survey.pages[i]
                        if (currentPage) {
                            for (var j = 0; j < currentPage.questions.length; j++) {
                                let currentQuestion = currentPage.questions[j]
                                if (currentQuestion && currentQuestion.questionTypeId == this.PredefineDropdown) {
                                    this.predefineDropdownItemsPage(currentQuestion)
                                    break
                                }
                            }
                        }
                    }
                    this.pageloading = false
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                    this.pageloading = false
                })
            },
            toggleShow() {
                this.show = !this.show
            },
            onImageFileChange(event, question) {
                const file = event.target.files[0]
                if (file && file.type.startsWith('image/')) {
                    const reader = new FileReader()
                    reader.onload = (e) => {
                        // Initialize cropper properties if not exist
                        if (!question.cropperImage) {
                            this.$set(question, 'cropperImage', '')
                            this.$set(question, 'showCropper', false)
                            this.$set(question, 'cropperZoom', 0)
                            this.$set(question, 'rotationAngle', 0)
                        }
                        // Set the image and show cropper
                        question.cropperImage = e.target.result
                        question.showCropper = true
                        question.cropperZoom = 0
                        question.rotationAngle = 0
                    }
                    reader.readAsDataURL(file)
                }
            },
            closeCropper(question) {
                question.showCropper = false
                question.cropperImage = null
                question.cropperZoom = 0
                question.rotationAngle = 0
                // Reset file input
                const fileInput = this.$refs['fileInput_' + question.id]
                if (fileInput && fileInput[0]) {
                    fileInput[0].value = ''
                }
            },
            saveCroppedImage(question) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    const { canvas } = cropperRef[0].getResult()
                    if (canvas) {
                        question.default = canvas.toDataURL('image/png', 0.9)
                        this.imgDataUrl = question.default
                        this.closeCropper(question)
                    }
                }
            },
            onZoomChange(question) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    cropperRef[0].zoom(question.cropperZoom)
                }
            },
            rotateImage(question, angle) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    cropperRef[0].rotate(angle)
                }
            },
            flipImageHorizontal(question) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    cropperRef[0].flip(true, false)
                }
            },
            flipImageVertical(question) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    cropperRef[0].flip(false, true)
                }
            },
            resetCropper(question) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    cropperRef[0].reset()
                    question.cropperZoom = 0
                    question.rotationAngle = 0
                }
            },
            cropSuccess(question, imgDataUrl) {
                this.imgDataUrl = imgDataUrl
                question.default = imgDataUrl
            },
            cropUploadSuccess(jsonData, field) {

            },
            cropUploadFail(status, field) {

            },
            handleFileUpload(question) {
                if (question.selectedFile) {
                    const file = question.selectedFile
                    if (file) {
                        const reader = new FileReader()
                        reader.onload = () => {

                            question.default = reader.result
                        }
                        reader.readAsDataURL(file)
                    }
                }
            },
            predefineDropdownItemsPage(currentQuestion) {
                LookupAdminService.listSurveyLookupItems(currentQuestion.lookupId)
                    .then((res) => {
                        currentQuestion.lookupoptions = []
                        res.data.forEach((el) => {
                            currentQuestion.lookupoptions.push({ id: el.id.toString(), name: el.name })
                        })
                    })
                    .catch((error) => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
            },
            hasUpdatePermission() {
                UsersService.listCurrentUserPermissions().then((res) => {
                    if (res.data) {
                        this.hasUpdatePublishedPermission = res.data.some(userPermission => userPermission.name === 'UpdatePublishedSurvey')

                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            createImage(value, question) {

                this.defaultValue = value + '%'
                this.imageWidth = value
                question.imageWidth = value
                var vImgComponent = this.$refs['img_' + question.id][0]
                setTimeout(function () {
                    var div = vImgComponent.$el.querySelector('.v-image__image')
                    var style = getComputedStyle(vImgComponent.$el)
                    var imgheight = parseInt(style.height)
                    var imgwidth = parseInt(style.width)
                    var imageLoader = new Image()
                    imageLoader.height = imgheight
                    imageLoader.onload = function () {
                        var imgHeight = imgheight
                        var imgWidth = 'auto'
                        var canvas = document.createElement('canvas')
                        var ctx = canvas.getContext('2d')
                        canvas.width = imgWidth
                        canvas.height = imgHeight
                        ctx.drawImage(imageLoader, 0, 0, imgwidth, imgheight)
                    }
                    imageLoader.src = getComputedStyle(div).backgroundImage.replace(/url\((['"])?(.*?)\1\)/gi, '$2')
                }, 500)
            },
            handleCopyValue(sourcePath, targetPath) {
                copyValue(sourcePath, targetPath, this, false)
            },
            getMunicipality() {
                StructuresService.listMunicipalities().then((res) => {
                    if (res.data) {
                        this.itemsMunicipality = res.data.map(item => ({
                            id: item.id,
                            municipalityName: item.name
                        }))
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            removeItem(id) {
                const index = this.selectedMunicipalityItems.indexOf(id)
                if (index !== -1) {
                    this.selectedMunicipalityItems.splice(index, 1)
                }
            },
            selectAllItems() {
                this.selectedMunicipalityItems = this.itemsMunicipality.map(item => item.id)
            },
            changeQuestionType(question, event){
            this.selectedMethod = event
            question.questionTypeId = event
                question.questionTypeParams = ''
                if (question.questionTypeId == this.CSAT5) {
                    this.addCSAT5Options(question)
                }
                else if (question.questionTypeId == this.CSAT3) {
                    this.addCSAT3Options(question)
                }
            },
            scrollToFirstInvalidField() {
                // Use nextTick to ensure the DOM has been updated with validation errors
                this.$nextTick(() => {
                    // Find the first element with error class
                    const errorElement = document.querySelector('.v-input.error--text, .v-text-field.error--text, .error--text')
                    
                    if (errorElement) {
                        // If the element is in a collapsed section, expand it first
                        if (!this.showAdditionalFields) {
                            // Check if the error element is within the collapsible section
                            const collapsibleSection = errorElement.closest('.v-expand-transition')
                            if (collapsibleSection) {
                                this.showAdditionalFields = true
                                // Wait for the expansion animation to complete before scrolling
                                this.$nextTick(() => {
                                    this.performScroll(errorElement)
                                })
                                return
                            }
                        }
                        
                        this.performScroll(errorElement)
                    }
                })
            },
            performScroll(element) {
                // Scroll to the element with smooth behavior and offset for better visibility
                const yOffset = -100 // Offset from the top of the viewport
                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
                
                window.scrollTo({
                    top: y,
                    behavior: 'smooth'
                })
                
                // Focus the input field if possible
                const input = element.querySelector('input, textarea, select')
                if (input) {
                    setTimeout(() => {
                        input.focus()
                    }, 500) // Wait for scroll animation
                }
            },
            performScrollToTop() {
                // Scroll to the element with smooth behavior and offset for better visibility
                
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })

                
            }
        },
    }
</script>

<style>
    .v-expansion-panel-header {
        border-bottom: 2px solid green;
        background: #f7f7f7;
        height: 40px
    }

    .preview-image {
        width: 150px; /* Adjust the size as needed */
        height: 150px; /* Adjust the size as needed */
    }

    .tiptap {
        height: 100%;
        padding: 10px
    }

    .cropper-wrapper {
        height: 500px;
        background: #DDD;
    }
</style>